<script lang="ts" setup>
import { useDefaultStore } from "@/store";
import api from "@/api";
import { useToast } from "primevue/usetoast";
import { $t } from "@/i18n";
import { debounce } from "@/assets/js/helpers";
import { useConfirm } from "primevue/useconfirm";
// import type { IUserList, IPermissions } from "@/api/types";
import { useSessionStore } from "@/store/session";

definePage({
	name: "ManageTags",
	meta: {
		name: "Tags Management",
	},
});

const confirm = useConfirm();
const toast = useToast();
const store = useDefaultStore();
const sessionStore = useSessionStore();
const searchValue = ref<string | null>(null);
const selectedProduct = ref();
const loadingDelete = ref(false);
const editingRows = ref([]);

const onRowEditSave = async (event) => {
	await editTag(event.newData.tag_id, event.newData.tag_label);
};

const getTagsDebounce = () => {
	debounce(store.getTags(searchValue.value), 300, true);
};

async function deleteTag(tag_id: number) {
	loadingDelete.value = true;
	const params = {
		tag_id,
	};
	try {
		const res = await api.tagsRemove(params);
		toast.add({
			severity: "success",
			summary: $t("app.manageTags.success.deleteTag.title"),
			detail: $t("app.manageTags.success.deleteTag.msg"),
			life: 3000,
		});
		await store.getTags();
		console.log(res.data.data);
	} catch (err: any) {
		if (
			err.response.data.message.includes("not integer number") ||
			err.response.data.message.includes("required")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.manageTags.error.invalidId.title"),
				detail: $t("app.manageTags.error.invalidId.msg"),
				life: 3000,
			});
		} else if (
			err.response.data.message.includes("Token has been blacklisted.")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.tokenBlacklisted.title"),
				detail: $t("app.auth.error.tokenBlacklisted.msg"),
				life: 3000,
			});
			try {
				await sessionStore.logoutApi();
			} catch (err: any) {
				console.warn(err.message);
			}
		} else if (err.response.data.message.includes("Account is suspended.")) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.accSuspended.title"),
				detail: $t("app.auth.error.accSuspended.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Tag not found")) {
			toast.add({
				severity: "error",
				summary: $t("app.manageTags.error.notFound.title"),
				detail: $t("app.manageTags.error.notFound.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Unauthorized")) {
			toast.add({
				severity: "error",
				summary: $t("app.permissions.deleteTag.title"),
				detail: $t("app.permissions.deleteTag.msg"),
				life: 3000,
			});
		} else {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.default.title"),
				detail: $t("app.postForm.error.default.msg"),
				life: 3000,
			});
		}
		console.warn(err.message);
	} finally {
		loadingDelete.value = false;
	}
}

const confirmDelete = (tag_id: number) => {
	confirm.require({
		message: $t("app.manageTags.deleteTag.msg"),
		header: $t("app.manageTags.deleteTag.header"),
		rejectLabel: $t("app.basic.cancel"),
		rejectProps: {
			label: $t("app.basic.cancel"),
			severity: "secondary",
			outlined: true,
		},
		acceptProps: {
			label: $t("app.basic.delete"),
			severity: "danger",
		},
		accept: async () => {
			await deleteTag(tag_id);
		},
	});
};

async function editTag(tag_id: number, tag_label: string | null = null) {
	const params = {
		tag_id,
		tag_label,
	};

	try {
		const res = await api.tagsEdit(params);
		toast.add({
			severity: "success",
			summary: $t("app.manageTags.success.editLabel.title"),
			detail: $t("app.manageTags.success.editLabel.msg"),
			life: 3000,
		});
		store.getTags();
		console.warn(res.data);
	} catch (err: any) {
		if (
			err.response.data.message.includes("not integer number") ||
			err.response.data.message.includes("required")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.manageTags.error.invalidId.title"),
				detail: $t("app.manageTags.error.invalidId.msg"),
				life: 3000,
			});
		} else if (err.response.data.message.includes("Tag not found")) {
			toast.add({
				severity: "error",
				summary: $t("app.manageTags.error.notFound.title"),
				detail: $t("app.manageTags.error.notFound.msg"),
				life: 3000,
			});
		} else if (
			err.response.data.message.includes("Token has been blacklisted.")
		) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.tokenBlacklisted.title"),
				detail: $t("app.auth.error.tokenBlacklisted.msg"),
				life: 3000,
			});
			try {
				await sessionStore.logoutApi();
			} catch (err: any) {
				console.warn(err.message);
			}
		} else if (err.response.data.message.includes("Account is suspended.")) {
			toast.add({
				severity: "error",
				summary: $t("app.auth.error.accSuspended.title"),
				detail: $t("app.auth.error.accSuspended.msg"),
				life: 3000,
			});
		} else {
			toast.add({
				severity: "error",
				summary: $t("app.postForm.error.default.title"),
				detail: $t("app.postForm.error.default.msg"),
				life: 3000,
			});
		}
		console.warn(err.message);
	}
}
</script>

<template>
	<div class="admin-section-outer-wrapper">
		<div class="admin-section-inner-wrapper">
			<div class="admin-section-header-wrapper">
				<div class="admin-section-header-heading">
					{{ $t("app.manageTags.title") }}
				</div>
				<div class="admin-section-header-filters-wrapper">
					<IconField
						:pt="{
							root: {
								style: 'width: 333px; max-width: 90%; height: 38px;',
							},
						}"
					>
						<InputIcon>
							<i-fa-magnifying-glass />
						</InputIcon>
						<InputText
							v-model="searchValue"
							@value-change="getTagsDebounce"
							:placeholder="$t('app.manageTags.searchPlaceholder')"
							fluid
						/>
					</IconField>
					<create-tag @create-tag="store.getTags()" />
				</div>
			</div>
			<div
				class="admin-section-content-wrapper"
				style="max-width: 600px"
			>
				<DataTable
					v-if="!store.tagsList"
					:value="[
						{ id: 1 },
						{ id: 2 },
						{ id: 3 },
						{ id: 4 },
						{ id: 5 },
						{ id: 6 },
						{ id: 7 },
						{ id: 8 },
						{ id: 9 },
						{ id: 10 },
					]"
					stripedRows
					scrollable
					scrollHeight="500px"
				>
					<Column
						:header="$t('app.manageTags.nameLabel')"
						style="width: 100%"
					>
						<template #body>
							<Skeleton height="29px"></Skeleton>
						</template>
					</Column>
					<Column style="width: 82px">
						<template #body>
							<Button
								severity="danger"
								variant="outlined"
								rounded
							>
								<template #icon>
									<i-fa-trash-can></i-fa-trash-can>
								</template>
							</Button>
						</template>
					</Column>
				</DataTable>
				<DataTable
					v-else
					v-model:editingRows="editingRows"
					:value="store.tagsList"
					stripedRows
					selectionMode="single"
					v-model:selection="selectedProduct"
					scrollable
					scrollHeight="500px"
					:virtualScrollerOptions="{ itemSize: 50 }"
					editMode="row"
					@row-edit-save="onRowEditSave"
				>
					<Column
						field="tag_label"
						:header="$t('app.manageTags.nameLabel')"
						style="width: 100%"
					>
						<template #editor="{ data, field }">
							<InputText v-model="data[field]" />
						</template>
					</Column>
					<Column
						v-if="sessionStore.can('edit-tag')"
						:rowEditor="true"
						style="min-width: 120px"
						bodyStyle="text-align:end"
					></Column>
					<Column
						v-if="sessionStore.can('delete-tag')"
						style="display: flex; justify-content: center; width: 82px"
					>
						<template #header><i-fa-trash-can></i-fa-trash-can></template>
						<template #body="{ data }">
							<Button
								severity="danger"
								variant="outlined"
								rounded
								@click="confirmDelete(data.tag_id)"
								:loading="loadingDelete"
							>
								<template #icon>
									<i-fa-trash-can></i-fa-trash-can>
								</template>
							</Button>
						</template>
					</Column>
				</DataTable>
			</div>
		</div>
	</div>
</template>

<style lang="scss">
.p-virtualscroller-inline {
	&::-webkit-scrollbar {
		background-color: $scrollbar-color;
		width: 5px;
		height: 5px;
	}

	&::-webkit-scrollbar-thumb {
		background-color: $scrollbar-color-thumb;
		border-radius: 5px;
	}
}

.p-datatable-column-title {
	font-size: 11px;
	font-weight: 700;
	line-height: 12.89px;
	color: $text-color-lighter;
}
</style>
